import { useState } from "react";
import { CheckIcon } from "lucide-react";

import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { EmptyDataDash } from "@/components/EmptyDataDash";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import { TableCell } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useUpdateSetPlacementMadeIt } from "@/hooks/updateSets/useUpdateSetPlacementMadeIt";
import { dates } from "@/lib/dates";
import { earningsTracker } from "@/lib/earningsTracker";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { EarningsEmploymentStatus, EarningsPlacement } from "@/types";

// Retention column: countdown pill, then a "Made it?" confirmation once the
// guarantee passes, then the confirmed green badge.
export const EarningsTrackerRetentionCell = ({
  placement,
}: {
  placement: EarningsPlacement;
}) => {
  /* Database CRUD Operations */
  // Update
  const {
    mutate: confirmMadeIt,
    isPending,
    isError,
  } = useUpdateSetPlacementMadeIt();

  /* React Hooks */
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  /* Helpers */
  const formattedRetentionDate = dates.formatIsoDateForUser(
    earningsTracker.getRetentionDateIso(placement),
  );
  const daysUntil = earningsTracker.getDaysUntilRetention(placement);
  const isChurned =
    earningsTracker.getEmploymentStatus(placement) ===
    EarningsEmploymentStatus.CHURNED;
  const guaranteeLabel = `${String(placement.backfillGuaranteeDays)}-day mark`;

  /* Handlers */
  const handleConfirmMadeIt = () => {
    mixpanelUtils.trackButtonClick(
      "Earnings confirm made it",
      MIXPANEL_SCREENS.EARNINGS,
    );
    confirmMadeIt(
      { placementId: placement.id },
      {
        onSuccess: () => {
          setIsConfirmDialogOpen(false);
        },
      },
    );
  };

  /* Render */
  const render = () => {
    // No clock until the start is confirmed; churned placements have none to run
    if (
      !placement.dateStarted ||
      isChurned ||
      !formattedRetentionDate ||
      daysUntil === null
    ) {
      return (
        <TableCell>
          <EmptyDataDash />
        </TableCell>
      );
    }

    if (placement.confirmedPastBackfillGuarantee) {
      return (
        <TableCell>
          <Tooltip>
            <TooltipTrigger asChild>
              <Row className="h-6 w-fit cursor-default items-center gap-1 whitespace-nowrap rounded-full border border-green-200 bg-green-50 px-2">
                <CheckIcon className="h-3 w-3 text-green-700" />
                <Text className="text-xs font-medium text-green-700">
                  Made it
                </Text>
              </Row>
            </TooltipTrigger>
            <TooltipContent>
              Reached {guaranteeLabel} on {formattedRetentionDate}
            </TooltipContent>
          </Tooltip>
        </TableCell>
      );
    }

    if (earningsTracker.isPastRetentionDate(placement)) {
      return (
        <TableCell>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 whitespace-nowrap rounded-full border-yellow-300 bg-yellow-50 px-2 text-xs font-medium text-yellow-800 hover:bg-yellow-100 hover:text-yellow-800"
                onClick={() => {
                  setIsConfirmDialogOpen(true);
                }}
              >
                Made it?
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Confirm with your placement that they&apos;re still at the client
            </TooltipContent>
          </Tooltip>
          <ConfirmationDialog
            open={isConfirmDialogOpen}
            onOpenChange={setIsConfirmDialogOpen}
            title="Confirm they made it"
            description={`Marking this complete will mark all placement check-in tasks (30 day, 60 day, etc) as complete. Are you sure you confirmed with the placement that they're still at ${placement.companyName}?`}
            confirmButtonText="They made it"
            isLoading={isPending}
            isError={isError}
            errorMessage="Failed to confirm. Try again."
            onConfirm={handleConfirmMadeIt}
            variant="square"
          />
        </TableCell>
      );
    }

    const daysLabel =
      daysUntil === 1 ? "1 day left" : `${String(daysUntil)} days left`;

    return (
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <Row className="h-6 w-fit cursor-default items-center whitespace-nowrap rounded-full border border-slate-200 bg-slate-50 px-2">
              <Text className="text-xs font-medium text-slate-700">
                {daysLabel}
              </Text>
            </Row>
          </TooltipTrigger>
          <TooltipContent>
            {guaranteeLabel} on {formattedRetentionDate}
          </TooltipContent>
        </Tooltip>
      </TableCell>
    );
  };

  return render();
};
